import { Typography } from "@mui/material";
import { orderBy } from "lodash";
import { DateTime } from "luxon";
import { ReactElement } from "react";

import {
  CampaignEventType,
  CampaignInitialSendScheduleEvent,
  CampaignStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";

import InitialSendComponents from "./initialSend";
import { CampaignForView } from "./types";

const campaignEventTimeDisplay = ({
  campaign,
  dateField,
  eventTypes,
}: {
  campaign: CampaignForView;
  dateField?: keyof CampaignForView;
  eventTypes?: CampaignEventType[];
}): string => {
  const dateAsMillis = dateField
    ? campaign[dateField]
    : eventTypes
      ? orderBy(campaign.events, "createdAt", "desc").find(event =>
          eventTypes.includes(event.type)
        )?.createdAt
      : undefined;

  return (
    dateAsMillis &&
    DateTime.fromMillis(dateAsMillis).toLocaleString(DateTime.DATETIME_FULL)
  );
};

const campaignStatusDisplay: Record<
  CampaignStatus,
  (campaign: CampaignForView, initialSentCount?: number) => string
> = {
  [CampaignStatus.InitialSendReady]: (
    campaign: CampaignForView,
    initialSentCount?: number
  ) => {
    if (campaign.texterSendEnabled) {
      return "Texter Send Turned On";
    }

    const notStarted = "Not started";
    const eventsToLookFor = [
      CampaignEventType.InitialSendCompletePartial,
      CampaignEventType.InitialSendCancelError,
      CampaignEventType.InitialSendCancelErrorRate,
      CampaignEventType.InitialSendCancelManual,
      CampaignEventType.InitialSendCancelTextingHours,
      CampaignEventType.InitialSendCancelSendingHold,
      CampaignEventType.InitialSendCancelBudgetExceeded,
    ];
    const mostRecentEvent = orderBy(
      campaign.events.filter(e => eventsToLookFor.includes(e.type)),
      "createdAt",
      "desc"
    )[0];

    // 0 or undefined
    if (!initialSentCount) {
      return notStarted;
    }

    if (!mostRecentEvent) {
      return "In progress...";
    }

    const mostRecentEventTime = DateTime.fromMillis(
      mostRecentEvent.createdAt
    ).toLocaleString(DateTime.DATETIME_FULL);

    switch (mostRecentEvent.type) {
      case CampaignEventType.InitialSendCompletePartial:
        return `Partial send finished at ${mostRecentEventTime}`;
      case CampaignEventType.InitialSendCancelError:
      case CampaignEventType.InitialSendCancelErrorRate:
      case CampaignEventType.InitialSendCancelManual:
      case CampaignEventType.InitialSendCancelTextingHours:
      case CampaignEventType.InitialSendCancelSendingHold:
      case CampaignEventType.InitialSendCancelBudgetExceeded:
        return `Canceled at ${mostRecentEventTime}`;
      default:
        return notStarted;
    }
  },
  [CampaignStatus.InitialSendRunning]: (campaign: CampaignForView) => {
    if (campaign.texterSendEnabled) {
      return "Texter Send Turned On";
    } else {
      return `Running since ${campaignEventTimeDisplay({
        campaign,
        eventTypes: [CampaignEventType.InitialSendBegin],
      })}`;
    }
  },
  [CampaignStatus.InitialSendComplete]: (campaign: CampaignForView) =>
    `Finished on ${campaignEventTimeDisplay({
      campaign,
      eventTypes: [CampaignEventType.InitialSendCompleteFull],
    })}`,
  [CampaignStatus.InitialSendCanceling]: (campaign: CampaignForView) =>
    `Canceling as of ${campaignEventTimeDisplay({
      campaign,
      eventTypes: [
        CampaignEventType.InitialSendCancelManual,
        CampaignEventType.DisableTexterSend,
      ],
    })}`,
  [CampaignStatus.InitialSendScheduled]: (campaign: CampaignForView) => {
    const scheduleEvent = campaign.events.find(
      event => event.type === CampaignEventType.InitialSendSchedule
    )! as CampaignInitialSendScheduleEvent;
    const prefix = scheduleEvent.data.partialSendQty
      ? `Scheduled to send ${scheduleEvent.data.partialSendQty.toLocaleString()} messages at`
      : "Scheduled to start";
    return `${prefix} ${campaignEventTimeDisplay({
      campaign,
      dateField: "initialSendScheduledFor",
    })}`;
  },
  [CampaignStatus.Draft]: () => "Draft",
  [CampaignStatus.Archived]: (campaign: CampaignForView) =>
    `Archived on ${campaignEventTimeDisplay({
      campaign,
      dateField: "archivedAt",
    })}`,
  [CampaignStatus.RoiTestAutosendPending]: () => "Waiting for Winner",
};

export const blastStatusToDisplayText = (
  campaign: CampaignForView,
  initialSentCount: number
): string => {
  return campaignStatusDisplay[campaign.status](campaign, initialSentCount);
};

export const BlastSendStatus = ({
  campaign,
  initialSentCount = 0,
  hasActBlueIntegration,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  initialSentCount?: number;
  hasActBlueIntegration: boolean;
  revealSendStatus: () => void;
}): ReactElement => {
  const InitialSendComponent = InitialSendComponents[campaign.status];
  const statusLabel = blastStatusToDisplayText(campaign, initialSentCount);

  return (
    <FlexBox direction="column" gap={1} sx={{ mt: 4 }}>
      <Typography variant="h1" gutterBottom>
        <strong>Blast Send Status:</strong> {statusLabel}
      </Typography>

      {InitialSendComponent && (
        <InitialSendComponent
          campaign={campaign}
          hasActBlueIntegration={hasActBlueIntegration}
          revealSendStatus={revealSendStatus}
        />
      )}
    </FlexBox>
  );
};
